import {Button} from '@dabafinance/react-components';
import styles from './customerCallList.module.scss';
import {
  Form,
  Select,
  Table,
  InputNumber,
  Card,
  Col,
  Divider,
  Typography,
  Collapse,
  Switch,
  Tag,
  Pagination,
  Tooltip,
  Input,
} from 'antd';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {GET_CUSTOMERS_CALL_LIST} from 'pages/customerSuccessHub/resolvers';
import {useQuery} from '@apollo/client';
import {toast} from 'react-toastify';
import moment from 'moment';
import {useState, useMemo} from 'react';
import DBModal from 'components/modal/modal';
import {client} from 'index';
import {UserSelectField} from 'components/userSelectField/UserSelectField';
import {useSelector} from 'react-redux';
import {NavTabsEnum, usersSegments} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';
import UserLink from 'components/userLink/UserLink';

const {Title, Text} = Typography;
const {Panel} = Collapse;

export const SalesAgentCallList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [filters, setFilters] = useState({});
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [exportForm] = Form.useForm();
  const [callReasonModal, setCallReasonModal] = useState({
    open: false,
    data: null,
  });
  const [activeKey, setActiveKey] = useState([]);
  const adminAgent = Form.useWatch('customerSuccessAgentId', form);
  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.SalesAgentCallList),
  );

  const {data, refetch, loading} = useQuery(GET_CUSTOMERS_CALL_LIST, {
    variables: {
      filters: {
        paginate: true,
        page: 1,
        limit: 10,
      },
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onError: error => toast.error(error.message),
  });

  const applyFilters = values => {
    setFilters(values);
    setCurrentPage(1);
    setLimit(10);
    setActiveKey([]);
    const {customerSuccessAgentId, ...rest} = values;
    refetch({
      filters: {
        page: 1,
        limit: 10,
        paginate: true,
        ...(customerSuccessAgentId && {
          customerSuccessAgentId: customerSuccessAgentId.split(';')[2],
        }),
        ...rest,
      },
    });
  };

  const handlePageChange = (page, pageSize) => {
    if (page === currentPage && pageSize === limit) return;
    setCurrentPage(page);
    setLimit(pageSize);
    const {customerSuccessAgentId, ...rest} = filters;
    refetch({
      filters: {
        page,
        limit: pageSize,
        paginate: true,
        ...(customerSuccessAgentId && {
          customerSuccessAgentId: customerSuccessAgentId.split(';')[2],
        }),
        ...rest,
      },
    });
  };

  return (
    <div className={styles.customerCallList}>
      <div className={styles.header}>
        <Title level={2}>Sales Agent Call List</Title>
        <Text type="secondary">
          Filter and prioritize customers for outreach based on investment
          behavior and portfolio status
        </Text>
      </div>

      <div className={styles.searchContainer}>
        <Button
          label="Export"
          onClick={() => setIsExportModalOpen(true)}
          disabled={!tabOperations?.export}
        />
      </div>

      <Collapse
        className={styles.filterCollapse}
        ghost
        activeKey={activeKey}
        onChange={setActiveKey}>
        <Panel
          header={<h2 className={styles.filterHeader}>Filter Criteria</h2>}
          key="1">
          <Card className={styles.filterCard}>
            <Form onFinish={applyFilters} layout="vertical" form={form}>
              <div className={styles.singleFilterRow}>
                <Col xs={24} sm={12} md={4} lg={9}>
                  <Form.Item label="Sales Agent" name="customerSuccessAgentId">
                    <UserSelectField
                      users={adminAgent}
                      mode="single"
                      setUsers={value =>
                        form.setFieldValue('customerSuccessAgentId', value)
                      }
                      placeholder="Search and Select Agent"
                      admin
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={4} lg={5}>
                  <Form.Item label="Asset Classes" name="assetClasses">
                    <Select
                      placeholder="Select Asset Class"
                      allowClear
                      mode="multiple"
                      options={assetClasses}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={4} lg={4}>
                  <Form.Item label="Call Priority" name="priority">
                    <Select
                      placeholder="Select Priority"
                      allowClear
                      options={callsPriority}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={4} lg={4}>
                  <Form.Item label="DabaPro Status" name="dabaProStatus">
                    <Select
                      placeholder="Select DabaPro Status"
                      allowClear
                      options={dabaProStatus}
                    />
                  </Form.Item>
                </Col>
              </div>
              <div className={styles.singleFilterRow}>
                <Col xs={24} sm={12} md={4} lg={9}>
                  <Form.Item label="User Segments" name="userSegments">
                    <Select
                      placeholder="Select User Segments"
                      mode="multiple"
                      allowClear
                      showSearch
                      options={usersSegments?.map(segment => ({
                        label: segment.value,
                        value: segment.key,
                      }))}
                    />
                  </Form.Item>
                </Col>
              </div>
              <div className={styles.filterRow}>
                <Form.Item
                  label="Has no Bonds/Managed Funds"
                  name="notInvestedInBondsOrManagedFunds">
                  <Switch />
                </Form.Item>
                <Form.Item
                  tooltip="Little Experience, Completed KYC"
                  label="KYC Beginners"
                  name="kycBeginners">
                  <Switch />
                </Form.Item>
                <Form.Item
                  tooltip="Portfolio with 10% or more losses in the last 30 days"
                  label="Portfolio with losses"
                  name="portfolioWithLosses">
                  <Switch />
                </Form.Item>
                <Form.Item
                  tooltip="Active Investments within the last 30 days"
                  label="Active Investors"
                  name="activeInvestments">
                  <Switch />
                </Form.Item>
              </div>
              <div className={styles.filterRow}>
                <Form.Item
                  className={styles.rangeInputs}
                  label="Portfolio % Change">
                  <Form.Item name="portfolioPercentageChangeMin">
                    <InputNumber
                      placeholder="Min %"
                      className={styles.rangeNumber}
                      parser={value => value.replace(/%/g, '')}
                      addonBefore="% Min"
                      min={-100}
                    />
                  </Form.Item>
                  <Form.Item name="portfolioPercentageChangeMax">
                    <InputNumber
                      placeholder="Max %"
                      className={styles.rangeNumber}
                      min={-100}
                      addonBefore="% Max"
                    />
                  </Form.Item>
                </Form.Item>
                <Form.Item
                  className={styles.rangeInputs}
                  label="Total Portfolio Value">
                  <Form.Item name="totalPortfolioValueMin">
                    <InputNumber
                      placeholder="Min CFA"
                      className={styles.rangeNumber}
                      formatter={value =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                      parser={value => value.replace(/\$\s?|(,*)/g, '')}
                      addonAfter="Min"
                      addonBefore="CFA"
                    />
                  </Form.Item>
                  <Form.Item name="totalPortfolioValueMax">
                    <InputNumber
                      placeholder="Max CFA"
                      className={styles.rangeNumber}
                      addonAfter="Max"
                      formatter={value =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                      parser={value => value.replace(/\$\s?|(,*)/g, '')}
                      addonBefore="CFA"
                    />
                  </Form.Item>
                </Form.Item>

                <Form.Item
                  label="Recent Significant Withdrawals"
                  className={styles.rangeInputs}
                  tooltip="Within the last 30 days">
                  <Form.Item name="recentSignificantWithdrawalMin">
                    <InputNumber
                      placeholder="Minimum"
                      className={styles.rangeNumber}
                      min={0}
                      addonAfter="Min"
                      addonBefore="CFA"
                      formatter={value =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                      parser={value => value.replace(/\$\s?|(,*)/g, '')}
                    />
                  </Form.Item>
                  <Form.Item name="recentSignificantWithdrawalMax">
                    <InputNumber
                      placeholder="Maximum"
                      className={styles.rangeNumber}
                      min={0}
                      addonAfter="Max"
                      addonBefore="CFA"
                      formatter={value =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                      parser={value => value.replace(/\$\s?|(,*)/g, '')}
                    />
                  </Form.Item>
                </Form.Item>
                <Form.Item
                  className={styles.rangeInputs}
                  label="Total Number of Investments">
                  <Form.Item name="totalInvestmentsMin">
                    <InputNumber
                      placeholder="Min"
                      className={styles.rangeNumber}
                      min={0}
                      addonBefore="Min"
                    />
                  </Form.Item>
                  <Form.Item name="totalInvestmentsMax">
                    <InputNumber
                      placeholder="Max"
                      addonBefore="Max"
                      className={styles.rangeNumber}
                      min={0}
                    />
                  </Form.Item>
                </Form.Item>
                <Form.Item
                  className={styles.rangeInputs}
                  label="Total No. of Investments in the last 30 days">
                  <Form.Item name="totalInvestmentsLast30DaysMin">
                    <InputNumber
                      placeholder="Min"
                      className={styles.rangeNumber}
                      min={0}
                      addonBefore="Min"
                    />
                  </Form.Item>
                  <Form.Item name="totalInvestmentsLast30DaysMax">
                    <InputNumber
                      placeholder="Max"
                      addonBefore="Max"
                      className={styles.rangeNumber}
                      min={0}
                    />
                  </Form.Item>
                </Form.Item>
                <Form.Item
                  className={styles.rangeInputs}
                  label="Total Wallet Balance">
                  <Form.Item name="totalWalletBalanceMin">
                    <InputNumber
                      placeholder="Min"
                      className={styles.rangeNumber}
                      min={0}
                      addonBefore="CFA"
                      addonAfter="Min"
                      formatter={value =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                      parser={value => value.replace(/\$\s?|(,*)/g, '')}
                    />
                  </Form.Item>
                  <Form.Item name="totalWalletBalanceMax">
                    <InputNumber
                      placeholder="Max"
                      addonBefore="CFA"
                      addonAfter="Max"
                      className={styles.rangeNumber}
                      min={0}
                      formatter={value =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                      parser={value => value.replace(/\$\s?|(,*)/g, '')}
                    />
                  </Form.Item>
                </Form.Item>
              </div>
              <Divider />

              <div className={styles.filterActions}>
                <SubmitButton smallWidth label="Apply Filters" />
                <Button
                  label="Reset Filters"
                  onClick={() => {
                    form.resetFields();
                    setFilters({});
                    setCurrentPage(1);
                    setLimit(10);
                    setActiveKey([]);
                    refetch({
                      filters: {
                        page: 1,
                        limit: 10,
                        paginate: true,
                      },
                    });
                  }}
                />
              </div>
            </Form>
          </Card>
        </Panel>
      </Collapse>

      <div className={styles.tableContainer}>
        <h2>Customers List</h2>
        <Table
          columns={tableColumns()}
          loading={loading}
          className={styles.table}
          dataSource={data?.getCustomerCallList?.data}
          pagination={false}
          onRow={record => ({
            onClick: () => setCallReasonModal({open: true, data: record}),
            style: {cursor: 'pointer'},
          })}
        />
        <Pagination
          current={currentPage}
          pageSize={limit}
          showSizeChanger
          showQuickJumper
          total={data?.getCustomerCallList?.pagination?.total}
          onChange={handlePageChange}
          className="ant-table-pagination"
        />
      </div>
      <DBModal
        isOpen={isExportModalOpen}
        handleClose={() => {
          setIsExportModalOpen(false);
          exportForm.resetFields();
        }}
        content={
          <ExportModal
            form={exportForm}
            filterValues={filters}
            onClose={() => {
              setIsExportModalOpen(false);
              exportForm.resetFields();
            }}
          />
        }
      />
      <DBModal
        isOpen={callReasonModal.open}
        width={1000}
        handleClose={() => setCallReasonModal({open: false, data: null})}
        content={<DetailsModal data={callReasonModal.data} />}
      />
    </div>
  );
};

const DetailsModal = ({data}) => {
  if (!data) {
    return (
      <div className={styles.callReasonModal}>
        <div className={styles.modalHeader}>
          <Title level={3} className={styles.modalTitle}>
            Customer Details
          </Title>
        </div>
        <div className={styles.modalContent}>
          <div className={styles.noReasons}>
            <Text type="secondary" className={styles.noReasonsText}>
              No customer data available.
            </Text>
          </div>
        </div>
      </div>
    );
  }

  const user = data.user;
  const customerSuccessAgent = data.customerSuccessAgent;

  const details = [
    {
      label: 'Customer Name',
      value: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'N/A',
    },
    {label: 'Email', value: user?.email || 'N/A'},
    {
      label: 'Phone Number',
      value: `${user?.countryCode || ''}${user?.phoneNumber || 'N/A'}`,
    },
    {label: 'Priority', value: data.priority?.replace(/_/g, ' ') || 'N/A'},
    {label: 'Call Notes', value: data.callReasons || 'N/A'},
    {
      label: 'Portfolio % Change',
      value: data.portfolioPercentageChange
        ? `${data.portfolioPercentageChange}%`
        : 'N/A',
    },
    {
      label: 'Risk Profile',
      value: data.investorProfile?.toUpperCase() || 'N/A',
    },
    {
      label: 'Last Investment Date',
      value: data.lastInvestmentDate
        ? moment(data.lastInvestmentDate).format('Do MMM, YYYY')
        : 'N/A',
    },
    {
      label: 'Total Investments',
      value: data.totalInvestments
        ? `${data.totalInvestments.toLocaleString()}`
        : 'N/A',
    },
    {
      label: 'Total Investments (30 days)',
      value: data.totalInvestmentsLast30Days
        ? `${data.totalInvestmentsLast30Days.toLocaleString()}`
        : 'N/A',
    },
    {
      label: 'Total Wallet Balance',
      value: data.totalWalletBalance
        ? `CFA ${data.totalWalletBalance.toLocaleString()}`
        : 'N/A',
    },
    {
      label: 'Sales Agent',
      value: customerSuccessAgent
        ? `${
            customerSuccessAgent.firstName || customerSuccessAgent.email || ''
          } ${customerSuccessAgent.lastName || ''}`.trim()
        : 'N/A',
    },

    {
      label: 'Daba Pro Status',
      value: data.dabaProStatus?.replace(/_/g, ' ') || 'N/A',
    },
    {
      label: 'Total Returns',
      value: data.totalReturns
        ? `CFA ${data.totalReturns.toLocaleString()}`
        : 'N/A',
    },
    {label: 'Has Losses', value: data.hasLosses ? 'Yes' : 'No'},
    {
      label: 'Has Recent Investments',
      value: data.hasRecentInvestments ? 'Yes' : 'No',
    },
    {
      label: 'Has Recent Withdrawals',
      value: data.hasRecentWithdrawals ? 'Yes' : 'No',
    },
    {
      label: 'Has Invested in Bonds/Managed Funds',
      value: data.hasInvestedInBondsOrManagedFunds ? 'Yes' : 'No',
    },
    {label: 'Total Bonds', value: data.totalBonds || '0'},
    {label: 'Total Managed Funds', value: data.totalManagedFunds || '0'},
    {label: 'Total Stocks', value: data.totalStocks || '0'},
    {label: 'Total Venture Funds', value: data.totalVentureFunds || '0'},
    {
      label: 'Invested Asset Classes',
      value:
        data.investedAssetClasses
          ?.map(asset => asset?.replace(/_/g, ' '))
          .join(', ') || 'N/A',
    },
    {
      label: 'User Segments',
      value:
        data.userSegments
          ?.map(segment => segment?.name?.replaceAll('_', ' ')?.toLowerCase())
          .join(', ') || 'N/A',
    },
  ];

  return (
    <div className={styles.callReasonModal}>
      <div className={styles.modalHeader}>
        <Title level={3} className={styles.modalTitle}>
          Customer Details
        </Title>
      </div>

      <div className={styles.modalContent}>
        <div className={styles.detailsGrid}>
          {details.map((detail, index) => (
            <div key={index} className={styles.detailItem}>
              <div className={styles.detailLabel}>{detail.label}</div>
              <div className={styles.detailValue}>{detail.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ExportModal = ({form, filterValues, onClose}) => {
  const [loading, setLoading] = useState(false);
  const adminEmail = Form.useWatch('adminEmail', form);

  const isEmailValid = useMemo(() => {
    if (!adminEmail) return true; // Empty email is allowed (optional field)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(adminEmail) && adminEmail.endsWith('@joindaba.com');
  }, [adminEmail]);
  const handleExport = values => {
    setLoading(true);
    client
      .query({
        query: GET_CUSTOMERS_CALL_LIST,
        variables: {
          filters: {
            export: true,
            ...(values.adminEmail && {emails: [values.adminEmail]}),
            ...filterValues,
          },
        },
      })
      .then(({data: {getCustomerCallList}}) => {
        if (getCustomerCallList.__typename === 'Error') {
          toast.error(getCustomerCallList.message);
        } else {
          toast.success(getCustomerCallList.message);
          onClose();
        }
      })
      .catch(error => {
        toast.error(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  return (
    <div className={styles.exportModal}>
      <h1>Export Customers</h1>
      <p> Please note that existing filters apply to exported data</p>
      <Form form={form} layout="vertical" onFinish={handleExport}>
        <Form.Item
          name="adminEmail"
          rules={[
            {
              type: 'email',
              message: 'Please enter a valid email address',
            },
            {
              validator: (_, value) => {
                if (!value) {
                  return Promise.resolve();
                }
                if (!value.endsWith('@joindaba.com')) {
                  return Promise.reject(
                    new Error('Only daba emails are allowed'),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
          label={
            <div>
              <Tooltip
                trigger="hover"
                title="If email is not provided, it will be exported to the email that you logged in with">
                Export Email (Optional)
              </Tooltip>
            </div>
          }>
          <Input ype="email" placeholder="Enter your email address" />
        </Form.Item>
        <SubmitButton
          disabled={loading || !isEmailValid}
          label={loading ? 'Exporting...' : 'Export'}
        />
      </Form>
    </div>
  );
};

const tableColumns = () => [
  {
    title: 'Customer Name',
    dataIndex: 'user',
    key: 'user',
    fixed: 'left',
    render: value => (
      <div
        style={{
          maxWidth: '120px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
        <UserLink userId={value?.id}>
          {value?.firstName} {value?.lastName}
        </UserLink>
      </div>
    ),
  },
  {
    title: 'Priority',
    dataIndex: 'priority',
    key: 'priority',
    render: value => (
      <Tag
        color={
          {
            VERY_HIGH: 'red',
            HIGH: 'blue',
            MODERATE: 'green',
            STANDARD: 'purple',
          }[value]
        }>
        {value?.replace(/_/g, ' ')}
      </Tag>
    ),
  },
  {
    title: 'Portfolio % Change',
    dataIndex: 'portfolioPercentageChange',
    key: 'portfolioPercentageChange',
    render: value => (
      <span
        className={
          value > 0
            ? styles.percentPositive
            : value < 0
            ? styles.percentNegative
            : undefined
        }>
        {value}%
      </span>
    ),
  },
  {
    title: 'Last Investment Date',
    dataIndex: 'lastInvestmentDate',
    key: 'lastInvestmentDate',
    render: value => (value ? moment(value).format('Do MMM, YYYY') : '-'),
  },
  {
    title: 'Total Investments',
    dataIndex: 'totalInvestments',
    key: 'totalInvestments',
    render: value => `CFA ${value?.toLocaleString()}`,
  },
  {
    title: 'Total Investments in the last 30 days',
    dataIndex: 'totalInvestmentsLast30Days',
    key: 'totalInvestmentsLast30Days',
    render: value => `CFA ${value?.toLocaleString()}`,
  },
  {
    title: 'Total Wallet Balance',
    dataIndex: 'totalWalletBalance',
    key: 'totalWalletBalance',
    render: value => `CFA ${value?.toLocaleString()}`,
  },
  {
    title: 'Sales Agent',
    dataIndex: 'customerSuccessAgent',
    key: 'customerSuccessAgent',
    render: value => (
      <div
        style={{
          maxWidth: '150px',
        }}>
        {value?.firstName ?? value?.email} {value?.lastName ?? ''}
      </div>
    ),
  },
  {
    title: 'Daba Pro Status',
    dataIndex: 'dabaProStatus',
    key: 'dabaProStatus',
    render: value => (
      <Tag
        color={
          {
            ACTIVE: 'green',
            EXPIRING_SOON: 'yellow',
            EXPIRED: 'red',
            NO_SUBSCRIPTION: 'gray',
          }[value]
        }>
        {value?.replace(/_/g, ' ')}
      </Tag>
    ),
  },
  {
    title: 'Total Returns',
    dataIndex: 'totalReturns',
    key: 'totalReturns',
    render: value => `CFA ${value?.toLocaleString()}`,
  },
  {
    title: 'Has Losses',
    dataIndex: 'hasLosses',
    key: 'hasLosses',
    render: value => (value ? 'Yes' : 'No'),
  },
  {
    title: 'Has Recent Investments',
    dataIndex: 'hasRecentInvestments',
    key: 'hasRecentInvestments',
    render: value => (value ? 'Yes' : 'No'),
  },
  {
    title: 'Has Recent Withdrawals',
    dataIndex: 'hasRecentWithdrawals',
    key: 'hasRecentWithdrawals',
    render: value => (value ? 'Yes' : 'No'),
  },
  {
    title: 'Has Invested in Bonds/Managed Funds',
    dataIndex: 'hasInvestedInBondsOrManagedFunds',
    key: 'hasInvestedInBondsOrManagedFunds',
    render: value => (value ? 'Yes' : 'No'),
  },
  {
    title: 'Total Bonds',
    dataIndex: 'totalBonds',
    key: 'totalBonds',
  },
  {
    title: 'Total Managed Funds',
    dataIndex: 'totalManagedFunds',
    key: 'totalManagedFunds',
  },
  {
    title: 'Total Stocks',
    dataIndex: 'totalStocks',
    key: 'totalStocks',
  },
  {
    title: 'Total Venture Funds',
    dataIndex: 'totalVentureFunds',
    key: 'totalVentureFunds',
  },
  {
    title: 'Invested Asset Classes',
    dataIndex: 'investedAssetClasses',
    key: 'investedAssetClasses',
    render: value => value?.map(asset => asset?.replace(/_/g, ' ')).join(', '),
  },
  {
    title: 'User Segments(s)',
    dataIndex: 'userSegments',
    key: 'userSegments',
    render: value =>
      value
        ?.map(segment => segment?.name?.replaceAll('_', ' ')?.toLowerCase())
        .join(', '),
  },
];

const dabaProStatus = [
  {
    value: 'ACTIVE',
    label: 'Active',
  },
  {
    value: 'EXPIRING_SOON',
    label: 'Expiring Soon',
  },
  {
    value: 'EXPIRED',
    label: 'Expired',
  },
  {
    value: 'NO_SUBSCRIPTION',
    label: 'No Subscription',
  },
];

const assetClasses = [
  {
    value: 'VENTURE',
    label: 'Venture',
  },
  {
    value: 'STOCK',
    label: 'Stock',
  },
  {
    value: 'VENTURE_FUND',
    label: 'Venture Fund',
  },
  {
    value: 'MUTUAL_FUND',
    label: 'Mutual Fund',
  },
  {
    value: 'BOND',
    label: 'Bond',
  },
];

const callsPriority = [
  {
    value: 'VERY_HIGH',
    label: 'Very High',
  },
  {
    value: 'HIGH',
    label: 'High',
  },
  {
    value: 'MODERATE',
    label: 'Moderate',
  },
  {
    value: 'STANDARD',
    label: 'Standard',
  },
];
